import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Trash2 } from "lucide-react";

const TARGETS = [
  { key: "orphan_tasks", label: "Orphan Tasks", desc: "Tasks linked to a project that no longer exists" },
  { key: "orphan_procurement", label: "Orphan Procurement", desc: "Procurement items linked to a missing project" },
  { key: "orphan_payments", label: "Orphan Payments", desc: "Payments linked to a missing project" },
  { key: "blank_projects", label: "Blank Projects", desc: "Projects with empty/whitespace client name" },
];

export default function CleanupTools() {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/cleanup/scan");
      setScan(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Scan failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const doDelete = async (target, label) => {
    const count = scan?.counts?.[target] || 0;
    if (!count) return;
    if (!window.confirm(`Delete ${count} ${label}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup/delete", { target });
      toast.success(`Deleted ${data.deleted} ${label}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  const total = scan ? Object.values(scan.counts || {}).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-4" data-testid="cleanup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Data Hygiene</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Cleanup Tools</h1>
          <p className="text-xs text-muted-foreground mt-1">Find and remove orphan / blank records left behind by imports or deletes.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={busy}>
          <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} />Rescan
        </Button>
      </div>

      <Card className="p-4 bg-card border-border">
        <div className="flex items-center gap-3">
          <div className={`h-11 w-11 rounded grid place-items-center ${total > 0 ? "bg-amber-500/15 border-amber-500/30" : "bg-emerald-500/15 border-emerald-500/30"} border`}>
            <AlertTriangle className={`h-5 w-5 ${total > 0 ? "text-amber-400" : "text-emerald-400"}`} />
          </div>
          <div>
            <div className="text-sm">{total > 0 ? `${total} record(s) need attention` : "Everything looks clean"}</div>
            <div className="text-xs text-muted-foreground">Scanned across tasks, procurement, payments and projects.</div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {TARGETS.map((t) => {
          const count = scan?.counts?.[t.key] || 0;
          const rows = scan?.[t.key] || [];
          return (
            <Card key={t.key} className="p-4 bg-card border-border" data-testid={`cleanup-${t.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-display font-semibold flex items-center gap-2">
                    {t.label}
                    <span className={`font-mono text-sm px-2 py-0.5 rounded border ${count > 0 ? "bg-amber-500/15 border-amber-500/30 text-amber-300" : "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"}`}>{count}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t.desc}</div>
                </div>
                {count > 0 && (
                  <Button variant="outline" size="sm" onClick={() => doDelete(t.key, t.label)} disabled={busy} data-testid={`cleanup-delete-${t.key}`}>
                    <Trash2 className="h-3 w-3 mr-1 text-red-400" />Delete all
                  </Button>
                )}
              </div>
              {rows.length > 0 && (
                <ul className="text-xs text-muted-foreground max-h-32 overflow-y-auto space-y-1 mt-2 border-t border-border pt-2">
                  {rows.slice(0, 8).map((r) => (
                    <li key={r.id} className="truncate">
                      {r.title || r.item_name || r.client_name || `#${r.id?.slice(0, 8)}`}
                      {r.project_id && <span className="ml-2 text-[10px]">→ missing project {r.project_id.slice(0, 8)}…</span>}
                    </li>
                  ))}
                  {rows.length > 8 && <li className="italic">…and {rows.length - 8} more</li>}
                </ul>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
